import { PeopleIcon, PersonIcon, PublicIcon } from '@econsult/econsult-ui/dist/assets';
import { STRINGS } from 'config';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { clearTemplateInTemplateManager, selectTemplateInTemplateManager } from 'store/action/templates';
import { useAppDispatch, useAppState } from 'store/configureStore';
import type { Template, TemplatesState } from 'store/reducer/templates';
import { ConfirmDeleteTemplateModal } from './DeleteModal';
import { List } from './List';
import { Modal, ModalTitle, ModalTitleAndClose, TemplateManagerModalContents } from './Modal';
import { TemplateManagerMain, TemplateManagerSideBar } from './ModalStyles';
import { NoPreview, Preview } from './Preview';
import { NewTemplateButton, Search, TemplateFilterTabs } from './SideBar';
import { NewTemplateButtonContainer } from './styles';
import { UnsavedTemplateChangesModal } from './unsavedChanges';

const { TEMPLATE } = STRINGS;
const { SHARED, PERSONAL, ALL } = TEMPLATE.TEMPLATE_TYPE;

export function filterTemplates(orderedTemplates: Template[], filterTabId: string, searchValue: string): Template[] {
  const query = searchValue?.toLowerCase().trim() || '';
  const isUnfiltered = filterTabId === ALL;

  return orderedTemplates.filter(template => {
    const matchesFilter = isUnfiltered || template.type === filterTabId;
    if (!query) return matchesFilter;
    const nameMatches = template.name.toLowerCase().includes(query);
    return matchesFilter && nameMatches;
  });
}

const TEMPLATE_PRIORITY: Record<string, number> = {
  [TEMPLATE.EMIS_WEB_TEMPLATE]: 0,
  [TEMPLATE.DEFAULT_TEMPLATE_NAME]: 1,
};

export function getOrderedTemplates(templates: Template[]): Template[] {
  return [...templates].sort((a: Template, b: Template) => {
    const priorityA = TEMPLATE_PRIORITY[a.name] ?? 2; // default = 2 (after EMIS 0 + Default 1)
    const priorityB = TEMPLATE_PRIORITY[b.name] ?? 2;

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    return a.name.localeCompare(b.name);
  });
}

export interface TemplateFilter {
  id: string;
  icon: React.ReactNode;
  label: string;
}

const idPrefix = 'templateManager';
const allFilter = {
  id: ALL,
  label: 'All',
  icon: <PublicIcon />,
};
const personalFilter = {
  id: PERSONAL,
  label: 'Personal',
  icon: <PersonIcon />,
};
const sharedFilter = {
  id: SHARED,
  label: 'Shared',
  icon: <PeopleIcon />,
};

const templateFilters: TemplateFilter[] = [allFilter, personalFilter, sharedFilter];

export const newTemplate: Template = {
  id: 'newTemplate',
  name: '',
  practiceRole: '',
  content: '',
  type: PERSONAL,
  transcriptionType: 'Consultation',
  isNewTemplate: true,
};

interface TemplateManagerModalProps {
  isOpen?: boolean;
  onClose: () => void;
}

export const TemplateManagerModal = ({ isOpen = false, onClose }: TemplateManagerModalProps) => {
  const [nextTemplateToBeSelected, setNextTemplateToBeSelected] = useState<(() => void) | null>();
  const [isUnsavedTemplateChangesModalOpen, setIsUnsavedTemplateChangesModalOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);
  const [isConfirmDeleteTemplateModalOpen, setIsConfirmDeleteTemplateModalOpen] = useState(false);
  const [templateFilter, setTemplateFilter] = useState<TemplateFilter>(allFilter);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    setTemplateFilter(allFilter);
    setSearchValue('');
  }, [isOpen]);
  const onChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => setSearchValue(event.target.value);

  const { templates: stateTemplates } = useAppState();
  const {
    template,
    templateHasUnsavedChanges,
    templates: rawTemplates,
    templatesLoadingState,
  } = stateTemplates as TemplatesState;
  const { id } = template;
  const templatesList: Template[] = rawTemplates;
  const orderedTemplates = getOrderedTemplates(templatesList);
  const selectedTemplateId = id || null;
  const dispatch = useAppDispatch();

  const handleClose = () => {
    if (templateHasUnsavedChanges) {
      setIsUnsavedTemplateChangesModalOpen(true);
      return;
    }
    dispatch(clearTemplateInTemplateManager());
    onClose();
  };
  const onClickFilterTab = (filter: TemplateFilter) => setTemplateFilter(filter);
  const clearSearch = () => setSearchValue('');
  const onClickNewTemplate = () => {
    if (templateHasUnsavedChanges) {
      setNextTemplateToBeSelected(() => () => dispatch(selectTemplateInTemplateManager(newTemplate)));
      return setIsUnsavedTemplateChangesModalOpen(true);
    }
    return dispatch(selectTemplateInTemplateManager(newTemplate));
  };
  const handleDiscard = () => {
    if (nextTemplateToBeSelected) {
      nextTemplateToBeSelected();
      setNextTemplateToBeSelected(null);
    } else {
      onClose();
    }
  };
  const onClickTemplate = (template: Template) => {
    const temp = {
      id: null,
      name: '',
      content: '',
      type: STRINGS.TEMPLATE.TEMPLATE_TYPE.PERSONAL,
    };
    const isDeselecting = selectedTemplateId === template.id;
    const templateToSelect = isDeselecting ? temp : template;

    if (templateHasUnsavedChanges) {
      setNextTemplateToBeSelected(() => () => dispatch(selectTemplateInTemplateManager(templateToSelect)));
      return setIsUnsavedTemplateChangesModalOpen(true);
    }

    return isDeselecting
      ? dispatch(clearTemplateInTemplateManager())
      : dispatch(selectTemplateInTemplateManager(template));
  };
  const handleClearTemplate = () => {
    if (templateHasUnsavedChanges) {
      setIsUnsavedTemplateChangesModalOpen(true);
      setNextTemplateToBeSelected(() => () => dispatch(clearTemplateInTemplateManager()));
      return;
    }
    dispatch(clearTemplateInTemplateManager());
  };
  const isAddingNewTemplate = selectedTemplateId === newTemplate.id;
  const filterTabId = templateFilter.id;
  const filteredTemplates = useMemo(
    () => filterTemplates(orderedTemplates, filterTabId, searchValue),
    [filterTabId, orderedTemplates, searchValue],
  );
  const selectedTemplateRef = useRef<HTMLLIElement>(null); // Placeholder for selected template reference
  const onSaveNewTemplate = () => {};
  const isDefaultTemplateSelected = template.name === TEMPLATE.DEFAULT_TEMPLATE_NAME && template.id !== 'newTemplate';
  return (
    <Modal
      onClose={handleClose}
      isOpen={isOpen}
      data-automation="TemplateManagerModal"
      aria-labelledby={`${idPrefix}-modal`}
      aria-describedby={`${idPrefix}-modal`}
      id={`${idPrefix}-modal`}
    >
      <ModalTitleAndClose role="banner">
        <ModalTitle data-automation="ModalTitle">Manage templates</ModalTitle>
      </ModalTitleAndClose>
      <TemplateManagerModalContents>
        <TemplateManagerSideBar>
          <TemplateFilterTabs
            filterTabId={filterTabId}
            onClickTemplateFilter={onClickFilterTab}
            templateFilters={templateFilters}
          />
          <Search
            searchValue={searchValue}
            onChangeSearch={onChangeSearch}
            clearSearch={clearSearch}
            idPrefix={idPrefix}
          />
          <List
            clearTemplate={handleClearTemplate}
            templatesLoadingState={templatesLoadingState}
            isFilteringOnSearchTerm={!!searchValue}
            isAddingNewTemplate={isAddingNewTemplate}
            filteredTemplates={filteredTemplates}
            selectedTemplateId={selectedTemplateId}
            onClickTemplate={onClickTemplate}
            idPrefix={idPrefix}
            newTemplate={newTemplate}
            selectedTemplateRef={selectedTemplateRef}
          />
          <NewTemplateButtonContainer data-automation="NewTemplateButtonContainer">
            <NewTemplateButton onClickNewTemplate={onClickNewTemplate} />
          </NewTemplateButtonContainer>
        </TemplateManagerSideBar>
        <TemplateManagerMain data-automation="TemplatePreview">
          {selectedTemplateId && !isDefaultTemplateSelected ? (
            <Preview
              idPrefix={idPrefix}
              isAddingNewTemplate={isAddingNewTemplate}
              onSaveNewTemplate={onSaveNewTemplate}
              templateId={selectedTemplateId}
              setIsConfirmDeleteTemplateOpen={setIsConfirmDeleteTemplateModalOpen}
              templateToDelete={templateToDelete}
            />
          ) : (
            <NoPreview />
          )}
        </TemplateManagerMain>
      </TemplateManagerModalContents>
      <UnsavedTemplateChangesModal
        isOpen={isUnsavedTemplateChangesModalOpen}
        onClose={() => setIsUnsavedTemplateChangesModalOpen(false)}
        onDiscard={handleDiscard}
        setNextTemplateToBeSelected={setNextTemplateToBeSelected}
      />
      <ConfirmDeleteTemplateModal
        isOpen={isConfirmDeleteTemplateModalOpen}
        onClose={() => setIsConfirmDeleteTemplateModalOpen(false)}
        templateToDelete={templateToDelete}
        setTemplateToDelete={setTemplateToDelete}
      />
    </Modal>
  );
};
